import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import adserver from 'services/adserver';
import {
  normalizeApiRequest,
  normalizeApiResponse,
} from './autosave.normalizers';
import { autosaveKeys } from './autosave.keys';

export interface AutosaveData {
  key: string;
  data: any;
}

const saveAutosaveApi = async (key: string, data: any): Promise<void> => {
  await adserver({
    url: '/form-autosaves/',
    method: 'POST',
    headers: {},
    data: { key, data },
  });
};

const loadAutosaveApi = async (key: string): Promise<any | null> => {
  try {
    const response = await adserver({
      url: `/form-autosaves/${key}`,
      method: 'GET',
      headers: {},
    });
    return response.data?.data || null;
  } catch (error) {
    return null;
  }
};

const deleteAutosaveApi = async (key: string): Promise<void> => {
  await adserver({
    url: `/form-autosaves/${key}`,
    method: 'DELETE',
    headers: {},
  });
};

export const useLoadAutosave = (
  key: string,
  enabled = true,
): UseQueryResult<any | null, unknown> => {
  return useQuery({
    queryKey: autosaveKeys.key(key),
    queryFn: () => loadAutosaveApi(key),
    select: data => normalizeApiResponse(data),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useSaveAutosave = (): UseMutationResult<
  void,
  unknown,
  { key: string; data: any },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, data }: { key: string; data: any }) => {
      const normalizedData = normalizeApiRequest(data);
      return saveAutosaveApi(key, normalizedData);
    },
    onSuccess: (_, { key, data }) => {
      const normalizedData = normalizeApiRequest(data);
      queryClient.setQueryData(
        autosaveKeys.key(key),
        normalizeApiResponse(normalizedData),
      );
    },
  });
};

export const useDeleteAutosave = (): UseMutationResult<
  void,
  unknown,
  string,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => deleteAutosaveApi(key),
    onSuccess: (_, key) => {
      queryClient.removeQueries(autosaveKeys.key(key));
    },
  });
};
